import { FAIconName, useToastMessage } from '@equip.health/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';

import {
  NoteCommunicationMethod,
  NoteStatus,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { NoteType } from '~/lib/constants/NoteType';
import { AppointmentTypeName } from '~/lib/constants/scheduling.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { getNoteTypeBasedOnAppointmentType } from '~/lib/util/notes/notes.util';
import {
  useCreateGroupClassNoteMutation,
  useCreateGroupSectionEventNoteMutation,
  useCreateNoteMutation,
} from '~/store/services/notes/notes';

interface UseAddNoteProps {
  appointmentExternalId?: string;
  appointmentType?: string;
  appointmentStartDateTime?: string;
  existingNoteId: string;
  groupClassEventExternalId?: string;
  groupClassEventTitle?: string;
  groupClassEventStartTime?: string;
  inquiryId?: string;
  noteId: string;
  patientId: string;
  setNoteId: (noteId: string) => void;
}

/** Custom hook to,
 * Create new note if existingNoteID is not available.
 * Get existing note detail,if existingNoteID is available.
 * Get option list for Note type dropdown.
 */
const useAddNote = ({
  appointmentExternalId,
  appointmentType,
  appointmentStartDateTime,
  existingNoteId,
  groupClassEventExternalId,
  groupClassEventStartTime,
  groupClassEventTitle,
  inquiryId,
  noteId,
  patientId,
  setNoteId,
}: UseAddNoteProps): {
  createNoteError: string;
} => {
  const { currentUser } = useGlobalContext();
  const { createToast } = useToastMessage();
  const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();

  const [createNote, { error: createNoteError }] = useCreateNoteMutation();
  const [createGroupClassNote, { error: createGroupClassNoteError }] =
    useCreateGroupClassNoteMutation();

  const [
    createGroupSectionEventNote,
    { error: createGroupSectionEventNoteError },
  ] = useCreateGroupSectionEventNoteMutation();

  /**
   * Create new note if existingNoteID is empty or fetch note detail for existing noteId
   * And fetch notes type dropdown options list form API
   */

  const getCommunicationMethod = () => {
    if (appointmentType == AppointmentTypeName.RECOVERY_NAVIGATOR_SESSION)
      return NoteCommunicationMethod.OUTBOUND_PHONE;
    return NoteCommunicationMethod.VIDEO;
  };

  useEffect(() => {
    if (noteId || existingNoteId) return;

    if (groupClassEventExternalId) {
      if (!groupClassEventStartTime) return;

      const payload = {
        activityDate: groupClassEventStartTime,
        activityWith: [],
        groupClassEventExternalId,
        isConfidential: false,
        noteCommunicationMethod: NoteCommunicationMethod.VIDEO,
        noteContent: '',
        noteStatus: NoteStatus.DRAFT,
        tags: [],
        title: groupClassEventTitle || '',
      };

      const action = isGroupsRestructuringEnabled
        ? createGroupSectionEventNote({
            ...payload,
            groupSectionEventExternalId: groupClassEventExternalId,
          }).unwrap()
        : createGroupClassNote(payload).unwrap();

      action
        .then((r) => {
          if (r?.noteExternalId) {
            setNoteId(r.noteExternalId);
          }
        })
        .catch(() => {
          createToast({
            icon: FAIconName.exclamationTriangleRegular,
            id: 'create-note-error_toast',
            text: "We're sorry, we were unable to create a note. Please try again later",
            variant: 'critical',
          });
        });
    } else {
      const noteType = inquiryId
        ? NoteType.ADMISSIONS
        : getNoteTypeBasedOnAppointmentType(
            appointmentType as AppointmentTypeName,
          );
      createNote({
        activityDate: appointmentStartDateTime,
        appointmentExternalId,
        authoredByProviderExternalId: currentUser.userId,
        noteCommunicationMethod: appointmentExternalId
          ? getCommunicationMethod()
          : undefined,
        noteType,
        patientExternalId: patientId,
      }).then((r) => setNoteId(r.data.noteExternalId));
    }
  }, [noteId, existingNoteId, groupClassEventStartTime]);

  const error =
    createNoteError ||
    createGroupClassNoteError ||
    createGroupSectionEventNoteError;

  return {
    createNoteError: error?.toString(),
  };
};

export default useAddNote;
